/**
 * 靜態股票資料服務
 * 使用預先生成的 JSON 檔案提供即時股票名稱查詢
 * 無需 API 呼叫，提供極快的查詢性能
 */

import { logger } from "../utils/logger";

export interface StaticStockInfo {
  symbol: string; // 股票代號 (如: 2330)
  name: string; // 股票名稱 (如: 台積電)
  fullName: string; // 完整名稱 (如: 台灣積體電路製造股份有限公司)
  market: "上市" | "上櫃" | "興櫃"; // 市場類型
  industry: string; // 產業類別
  isin: string; // ISIN 代碼
  listedDate: string; // 上市/上櫃日期
  key: string; // 識別鍵
}

export interface StaticStockMetadata {
  lastUpdated: string;
  totalStocks: number;
  listedCount: number;
  otcCount: number;
  symbolToName: Record<string, string>;
  nameToSymbol: Record<string, string>;
  symbolToStock: Record<string, StaticStockInfo>;
}

class StaticStockDataService {
  private allStocks: StaticStockInfo[] | null = null;
  private metadata: StaticStockMetadata | null = null;
  private symbolToStock: Map<string, StaticStockInfo> = new Map();
  private symbolToName: Map<string, string> = new Map();
  private nameToSymbol: Map<string, string> = new Map();
  private loaded = false;
  private loadPromise: Promise<void> | null = null;

  /**
   * 載入靜態股票資料檔案
   */
  private async loadStaticData(): Promise<void> {
    if (this.loaded) {
      return;
    }

    if (this.loadPromise) {
      await this.loadPromise;
      return;
    }

    this.loadPromise = (async () => {
      try {
        logger.api.info("Loading static stock data from JSON files");

        const allStocksResponse = await fetch('/data/all_stocks.json');
        if (!allStocksResponse.ok) {
          throw new Error(`Failed to load all_stocks.json: ${allStocksResponse.status}`);
        }
        this.allStocks = await allStocksResponse.json();

        const metadataResponse = await fetch('/data/stock_metadata.json');
        if (!metadataResponse.ok) {
          throw new Error(`Failed to load stock_metadata.json: ${metadataResponse.status}`);
        }
        this.metadata = await metadataResponse.json();

        this.allStocks?.forEach(stock => {
          this.symbolToStock.set(stock.symbol, stock);
          this.symbolToName.set(stock.symbol, stock.name);
          this.nameToSymbol.set(stock.name, stock.symbol);
        });

        this.loaded = true;

        logger.api.info("Static stock data loaded successfully", {
          totalStocks: this.allStocks?.length || 0,
          lastUpdated: this.metadata?.lastUpdated,
          listedCount: this.metadata?.listedCount,
          otcCount: this.metadata?.otcCount
        });
      } catch (error) {
        logger.api.error("Failed to load static stock data", { error });
        throw error;
      } finally {
        this.loadPromise = null;
      }
    })();

    await this.loadPromise;
  }

  /**
   * 確保資料已載入
   */
  private async ensureLoaded(): Promise<void> {
    if (!this.loaded) {
      await this.loadStaticData();
    }
  }

  /**
   * 根據股票代號前綴搜尋股票名稱 (相容原始 API)
   */
  async getStockNames(symbolPrefix: string): Promise<Array<{ symbol: string; name: string; key: string }>> {
    try {
      await this.ensureLoaded();

      const results: Array<{ symbol: string; name: string; key: string }> = [];

      // 搜尋符合前綴的股票
      for (const [symbol, stock] of Array.from(this.symbolToStock.entries())) {
        if (symbol.startsWith(symbolPrefix)) {
          results.push({
            symbol: stock.symbol,
            name: stock.name,
            key: stock.key,
          });
        }
      }

      logger.api.info("Static stock names search completed", {
        symbolPrefix,
        resultCount: results.length,
        source: "static_json"
      });

      return results;
    } catch (error) {
      logger.api.error("Error in static getStockNames", { symbolPrefix, error });
      return [];
    }
  }

  /**
   * 批量獲取股票名稱 (極快，無 API 呼叫)
   */
  async getBatchStockNames(symbols: string[]): Promise<Map<string, string>> {
    try {
      await this.ensureLoaded();

      const nameMap = new Map<string, string>();

      // 直接從靜態資料中查找
      symbols.forEach(symbol => {
        const stockName = this.symbolToName.get(symbol);
        if (stockName) {
          nameMap.set(symbol, stockName);
        }
      });

      logger.api.info("Static batch stock names completed", {
        requestedCount: symbols.length,
        resolvedCount: nameMap.size,
        coverage: `${((nameMap.size / symbols.length) * 100).toFixed(1)}%`,
        source: "static_json",
        additionalApiCalls: 0 // 永遠不需要 API 呼叫！
      });

      return nameMap;
    } catch (error) {
      logger.api.error("Error in static getBatchStockNames", { symbols, error });
      return new Map();
    }
  }

  /**
   * 根據股票代號獲取完整股票資訊
   */
  async getStockInfo(symbol: string): Promise<StaticStockInfo | null> {
    try {
      await this.ensureLoaded();
      return this.symbolToStock.get(symbol) || null;
    } catch (error) {
      logger.api.error("Error getting static stock info", { symbol, error });
      return null;
    }
  }

  /**
   * 同步獲取股票資訊 (只有當資料已載入時才會返回結果)
   * 用於需要同步存取的情況，例如 determineExchange
   */
  getStockInfoSync(symbol: string): StaticStockInfo | null {
    if (!this.loaded) {
      return null; // 資料尚未載入
    }
    return this.symbolToStock.get(symbol) || null;
  }

  /**
   * 根據股票名稱查找代號
   */
  async findSymbolByName(name: string): Promise<string | null> {
    try {
      await this.ensureLoaded();
      return this.nameToSymbol.get(name) || null;
    } catch (error) {
      logger.api.error("Error finding symbol by name in static data", { name, error });
      return null;
    }
  }

  /**
   * 獲取所有股票資料
   */
  async getAllStocks(): Promise<StaticStockInfo[]> {
    try {
      await this.ensureLoaded();
      return this.allStocks ? [...this.allStocks] : [];
    } catch (error) {
      logger.api.error("Error getting all static stocks", error);
      return [];
    }
  }

  /**
   * 根據市場類型獲取股票
   */
  async getStocksByMarket(market: "上市" | "上櫃"): Promise<StaticStockInfo[]> {
    try {
      await this.ensureLoaded();
      return this.allStocks?.filter(stock => stock.market === market) || [];
    } catch (error) {
      logger.api.error("Error getting stocks by market from static data", { market, error });
      return [];
    }
  }

  /**
   * 根據產業類別獲取股票
   */
  async getStocksByIndustry(industry: string): Promise<StaticStockInfo[]> {
    try {
      await this.ensureLoaded();
      return this.allStocks?.filter(stock => stock.industry === industry) || [];
    } catch (error) {
      logger.api.error("Error getting stocks by industry from static data", { industry, error });
      return [];
    }
  }

  /**
   * 搜尋股票名稱（模糊搜尋）
   */
  async searchStocksByName(query: string): Promise<StaticStockInfo[]> {
    try {
      await this.ensureLoaded();
      
      const lowerQuery = query.toLowerCase();
      return this.allStocks?.filter(stock => 
        stock.name.toLowerCase().includes(lowerQuery) ||
        stock.fullName.toLowerCase().includes(lowerQuery)
      ) || [];
    } catch (error) {
      logger.api.error("Error searching stocks by name", { query, error });
      return [];
    }
  }

  /**
   * 獲取所有產業類別
   */
  async getAllIndustries(): Promise<string[]> {
    try {
      await this.ensureLoaded();
      
      if (!this.allStocks) return [];
      
      const industries = new Set<string>();
      this.allStocks.forEach(stock => {
        if (stock.industry && stock.industry !== '未分類') {
          industries.add(stock.industry);
        }
      });
      
      return Array.from(industries).sort();
    } catch (error) {
      logger.api.error("Error getting all industries", error);
      return [];
    }
  }

  /**
   * 獲取資料狀態資訊
   */
  getDataStatus() {
    return {
      loaded: this.loaded,
      lastUpdated: this.metadata?.lastUpdated || null,
      totalStocks: this.metadata?.totalStocks || 0,
      listedCount: this.metadata?.listedCount || 0,
      otcCount: this.metadata?.otcCount || 0,
      source: "static_json_files"
    };
  }

  /**
   * 重新載入靜態資料
   */
  async reloadData(): Promise<void> {
    this.loaded = false;
    this.allStocks = null;
    this.metadata = null;
    this.symbolToStock.clear();
    this.symbolToName.clear();
    this.nameToSymbol.clear();
    this.loadPromise = null;
    
    await this.loadStaticData();
  }

  /**
   * 清除快取資料
   */
  clearCache(): void {
    this.loaded = false;
    this.allStocks = null;
    this.metadata = null;
    this.symbolToStock.clear();
    this.symbolToName.clear();
    this.nameToSymbol.clear();
    this.loadPromise = null;
    
    logger.api.info("Static stock data cache cleared");
  }
}

// 導出單例
export const staticStockDataService = new StaticStockDataService();
export default staticStockDataService;
